import styled, { createGlobalStyle } from 'styled-components';

export const colors = {
  bg: '#F7F3EC',
  surface: '#FDFBF7',
  ink: '#2A1E15',
  inkSoft: '#6B5B4D',
  accent: '#42281B',
  accentDeep: '#311C12',
  gold: '#B08D57',
  goldLight: '#E4D3B3',
  cream: '#EFE7D8',
  line: 'rgba(66, 40, 27, 0.16)',
};

export const fonts = {
  display: `'Playfair Display', 'Georgia', serif`,
  body: `'Jost', 'Helvetica Neue', Arial, sans-serif`,
};

export const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
};

export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }

  html {
    scroll-behavior: smooth;
  }

  body {
    margin: 0;
    padding: 0;
    background-color: ${colors.bg};
    color: ${colors.ink};
    font-family: ${fonts.body};
    font-size: 16px;
    line-height: 1.7;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  img {
    display: block;
    max-width: 100%;
  }

  a {
    color: inherit;
  }

  h1, h2, h3, h4 {
    font-family: ${fonts.display};
    font-weight: 500;
    margin: 0;
  }

  p {
    margin: 0;
  }

  ::selection {
    background: ${colors.goldLight};
    color: ${colors.accentDeep};
  }
`;

/** Centered page wrapper with generous horizontal padding. */
export const Page = styled.main`
  flex: 1;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 64px 32px 96px;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 40px 20px 72px;
  }
`;

/** Small uppercase label with letter-spacing, used above titles. */
export const Eyebrow = styled.div`
  font-family: ${fonts.body};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${colors.gold};
`;

export const SectionTitle = styled.h2`
  font-size: 34px;
  line-height: 1.2;
  color: ${colors.accent};

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 27px;
  }
`;

/** Thin decorative rule with a gold center diamond. */
export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 auto;
  max-width: 320px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${colors.line};
  }
`;

export const DividerDiamond = styled.span`
  width: 7px;
  height: 7px;
  background: ${colors.gold};
  transform: rotate(45deg);
  flex: none;
`;

/** Portrait image with an offset gold frame. */
export const FramedImage = styled.div<{ $ratio?: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${(p) => p.$ratio || '2 / 3'};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: relative;
    z-index: 1;
    box-shadow: 0 18px 40px rgba(42, 30, 21, 0.18);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translate(14px, 14px);
    border: 1px solid ${colors.gold};
    z-index: 0;
  }
`;

/** Elegant pull-quote with a gold side rule. */
export const PullQuote = styled.blockquote`
  margin: 0;
  padding: 4px 0 4px 22px;
  border-left: 2px solid ${colors.gold};
  font-family: ${fonts.display};
  font-style: italic;
  font-size: 19px;
  line-height: 1.6;
  color: ${colors.accent};

  footer {
    margin-top: 10px;
    font-family: ${fonts.body};
    font-style: normal;
    font-size: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${colors.inkSoft};
  }
`;
